export type ScoreOut = { id: number; rank: number; percentile: number; total?: number };
export type LeaderboardEntry = {
  rank: number;
  handle: string;
  score: number;
  runs: number;
  played_at: string;
};
export type RunStart = {
  run_id: string;
  seed: number;
  token: string;
  started_at: string;
};
export type RingEventOut = { t: number; ring_idx: number; passed: boolean };

const BASE = "/api";

async function jfetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

export async function fetchLeaderboard(
  limit = 10,
  game: "drift" | "drift-endless" = "drift",
): Promise<LeaderboardEntry[]> {
  try {
    return await jfetch<LeaderboardEntry[]>(`/leaderboard?limit=${limit}&game=${game}`);
  } catch {
    return [];
  }
}

export interface RuntimeConfig {
  turnstile_enabled: boolean;
  turnstile_sitekey: string | null;
}

export async function fetchConfig(): Promise<RuntimeConfig> {
  try {
    return await jfetch<RuntimeConfig>("/config");
  } catch {
    return { turnstile_enabled: false, turnstile_sitekey: null };
  }
}

export async function startRun(
  turnstileToken?: string | null,
  game: "drift" | "drift-endless" = "drift",
): Promise<RunStart | null> {
  try {
    // Always send a body now that 'game' carries information even when
    // Turnstile is off. Server defaults to 'drift' on a missing key, so
    // a stale client (pre-endless) still mints a valid timed run.
    const body = JSON.stringify({
      turnstile_token: turnstileToken ?? undefined,
      game,
    });
    return await jfetch<RunStart>("/runs", {
      method: "POST",
      body,
    });
  } catch {
    return null;
  }
}

interface FinishRunBody {
  run_id: string;
  handle: string;
  events: RingEventOut[];
}

export async function finishRun(body: FinishRunBody): Promise<ScoreOut | null> {
  try {
    return await jfetch<ScoreOut>(`/runs/${body.run_id}`, {
      method: "POST",
      body: JSON.stringify({ handle: body.handle, events: body.events }),
    });
  } catch {
    const queued: FinishRunBody[] = JSON.parse(localStorage.getItem("aether.queue") ?? "[]");
    queued.push(body);
    localStorage.setItem("aether.queue", JSON.stringify(queued));
    return null;
  }
}

export async function flushQueue(): Promise<void> {
  const queued: FinishRunBody[] = JSON.parse(localStorage.getItem("aether.queue") ?? "[]");
  if (!queued.length) return;
  const remaining: FinishRunBody[] = [];
  for (const item of queued) {
    // Drop legacy entries that pre-date the run-flow shape.
    if (!item.run_id || !item.events) continue;
    try {
      await jfetch(`/runs/${item.run_id}`, {
        method: "POST",
        body: JSON.stringify({ handle: item.handle, events: item.events }),
      });
    } catch {
      remaining.push(item);
    }
  }
  localStorage.setItem("aether.queue", JSON.stringify(remaining));
}
